/**
 * HIPAA-Compliant Session Management Service
 * Handles automatic logout, session tracking, and activity monitoring
 */
import { 
  doc, 
  setDoc, 
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from '../config/firebase.config';
import { createAuditLog, AUDIT_ACTIONS } from './audit.service';

// HIPAA requires automatic logout after 15 minutes of inactivity
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
const WARNING_BEFORE_TIMEOUT = 2 * 60 * 1000; // 2 minutes warning

class SessionService {
  constructor() {
    this.sessionId = null;
    this.lastActivity = Date.now();
    this.inactivityTimer = null;
    this.warningTimer = null;
    this.sessionListener = null;
    this.activityListeners = [];
    this.warningCallback = null;
    this.isActive = false;
  }

  /**
   * Initialize session management for the current user
   */
  async initializeSession(user) {
    if (!user) return;

    try {
      // Generate unique session ID
      this.sessionId = `${user.uid}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('sessionId', this.sessionId);

      // Create session document in Firestore
      const sessionRef = doc(db, 'sessions', this.sessionId);
      await setDoc(sessionRef, {
        userId: user.uid,
        userEmail: user.email,
        startTime: serverTimestamp(),
        lastActivity: serverTimestamp(),
        ipAddress: await this.getUserIP(),
        userAgent: navigator.userAgent,
        active: true
      });

      // Start monitoring session
      this.startActivityMonitoring();
      this.setupSessionListener(sessionRef);
      this.isActive = true;

      // Log session start
      await createAuditLog(AUDIT_ACTIONS.LOGIN, 'session/start', {
        sessionId: this.sessionId
      });

      console.log('Session initialized:', this.sessionId);
    } catch (error) {
      console.error('Failed to initialize session:', error);
    }
  }

  /**
   * Start monitoring user activity
   */
  startActivityMonitoring() {
    // Track various user activities
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    // Add event listeners
    activityEvents.forEach(event => {
      const listener = () => this.updateActivity();
      document.addEventListener(event, listener);
      this.activityListeners.push({ event, listener });
    });

    // Start inactivity timer
    this.resetInactivityTimer();
  }

  /**
   * Update last activity timestamp
   */
  async updateActivity() {
    const now = Date.now();
    
    // Only update if more than 30 seconds have passed (to avoid too many writes)
    if (now - this.lastActivity < 30000) return;

    this.lastActivity = now;
    this.resetInactivityTimer();

    // Update session in Firestore
    if (this.sessionId && this.isActive) {
      try {
        const sessionRef = doc(db, 'sessions', this.sessionId);
        await updateDoc(sessionRef, {
          lastActivity: serverTimestamp()
        });
      } catch (error) {
        console.error('Failed to update session activity:', error);
      }
    }
  }

  /**
   * Reset inactivity timer
   */
  resetInactivityTimer() {
    // Clear existing timers
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }

    // Set warning timer (2 minutes before timeout)
    this.warningTimer = setTimeout(() => {
      if (this.warningCallback) {
        this.warningCallback();
      }
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);

    // Set inactivity timer
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivityTimeout();
    }, INACTIVITY_TIMEOUT);
  }

  /**
   * Handle inactivity timeout
   */
  async handleInactivityTimeout() {
    console.log('Session timeout due to inactivity');
    
    // Log timeout
    await createAuditLog(AUDIT_ACTIONS.SESSION_TIMEOUT, 'session/timeout', {
      sessionId: this.sessionId,
      lastActivity: new Date(this.lastActivity).toISOString()
    });

    // Terminate session
    await this.terminateSession('timeout');
  }

  /**
   * Set warning callback for timeout warning
   */
  setWarningCallback(callback) {
    this.warningCallback = callback;
  }

  /**
   * Setup real-time listener for session changes
   */
  setupSessionListener(sessionRef) {
    this.sessionListener = onSnapshot(sessionRef, (doc) => {
      if (!doc.exists() || !doc.data().active) {
        // Session was terminated elsewhere
        console.log('Session terminated remotely');
        this.cleanup();
        signOut(auth);
      }
    });
  }

  /**
   * Terminate the current session
   */
  async terminateSession(reason = 'logout') {
    if (!this.sessionId || !this.isActive) return;

    try {
      this.isActive = false;

      // Update session as inactive
      const sessionRef = doc(db, 'sessions', this.sessionId);
      await updateDoc(sessionRef, {
        active: false,
        endTime: serverTimestamp(),
        endReason: reason
      });

      // Log session end
      await createAuditLog(AUDIT_ACTIONS.LOGOUT, 'session/end', {
        sessionId: this.sessionId,
        reason
      });

      // Clean up
      this.cleanup();

      // Sign out from Firebase
      await signOut(auth);
    } catch (error) {
      console.error('Failed to terminate session:', error);
      // Still try to sign out even if session update fails
      await signOut(auth);
    }
  }

  /**
   * Clean up session resources
   */
  cleanup() {
    // Clear timers
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    // Remove event listeners
    this.activityListeners.forEach(({ event, listener }) => {
      document.removeEventListener(event, listener);
    });
    this.activityListeners = [];

    // Unsubscribe from session listener
    if (this.sessionListener) {
      this.sessionListener();
      this.sessionListener = null;
    }

    // Clear session data
    sessionStorage.removeItem('sessionId');
    this.sessionId = null;
    this.isActive = false;
  }

  /**
   * Get user IP address
   */
  async getUserIP() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * Extend session (for active operations)
   */
  extendSession() {
    this.updateActivity();
  }

  /**
   * Get remaining time before timeout
   */
  getRemainingTime() {
    const elapsed = Date.now() - this.lastActivity;
    const remaining = INACTIVITY_TIMEOUT - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Check if session is about to expire
   */
  isAboutToExpire() {
    return this.getRemainingTime() < WARNING_BEFORE_TIMEOUT;
  }
}

// Create singleton instance
const sessionService = new SessionService();

// Export service and constants
export default sessionService;
export { INACTIVITY_TIMEOUT, WARNING_BEFORE_TIMEOUT }; 